import React, { useState } from 'react';
import {
    Card, CardContent, Typography, Grid, TextField, Button, Alert, MenuItem, Box, CircularProgress, Divider, Tooltip, InputAdornment
} from '@mui/material';
import axios from 'axios';
import config from '../../config/config.json';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import NumbersIcon from '@mui/icons-material/Numbers';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const host = config.production.host;

const ToolsGiornate = ({ leghe, stagioni, onSuccess }) => {
    const [form, setForm] = useState({
        legaId: '',
        stagioneId: '',
        numero_giornate_from: '',
        numero_giornate_to: '',
        nome_base_giornata: 'Giornata'
    });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [touched, setTouched] = useState({});

    const isValid = () => {
        return (
            form.legaId && form.stagioneId &&
            form.numero_giornate_from && form.numero_giornate_to &&
            form.nome_base_giornata.trim() &&
            Number(form.numero_giornate_from) > 0 &&
            Number(form.numero_giornate_to) >= Number(form.numero_giornate_from)
        );
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setTouched({ ...touched, [e.target.name]: true });
        setResult(null);
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setResult(null);
        setError(null);
        try {
            const jwt = localStorage.getItem('feathers-jwt');
            const res = await axios.post(
                `${host}/proc-crea-giornate-calendario`,
                {
                    legaId: form.legaId,
                    stagioneId: form.stagioneId,
                    numero_giornate_from: form.numero_giornate_from,
                    numero_giornate_to: form.numero_giornate_to,
                    nome_base_giornata: form.nome_base_giornata
                },
                { headers: { Authorization: `Bearer ${jwt}` } }
            );
            setResult(res.data);
            if (onSuccess) onSuccess();
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 6, maxWidth: 700, mx: 'auto', p: 1 }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                    <EventAvailableIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Creazione massiva giornate calendario
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Utilizza questo strumento per generare rapidamente le giornate di una lega/stagione.<br />
                    <b>⚠️ L'operazione è irreversibile</b> ma non crea duplicati.
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <form onSubmit={handleSubmit} autoComplete="off">
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Lega"
                                name="legaId"
                                value={form.legaId}
                                onChange={handleChange}
                                required
                                error={touched.legaId && !form.legaId}
                                helperText={touched.legaId && !form.legaId ? 'Seleziona una lega' : ' '}
                                InputProps={{ startAdornment: <InputAdornment position="start"><EmojiEventsIcon color="primary" /></InputAdornment> }}
                            >
                                {leghe?.map(l => (
                                    <MenuItem key={l.id} value={l.id}>{l.nome}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                select
                                fullWidth
                                label="Stagione"
                                name="stagioneId"
                                value={form.stagioneId}
                                onChange={handleChange}
                                required
                                error={touched.stagioneId && !form.stagioneId}
                                helperText={touched.stagioneId && !form.stagioneId ? 'Seleziona una stagione' : ' '}
                                InputProps={{ startAdornment: <InputAdornment position="start"><EventAvailableIcon color="action" /></InputAdornment> }}
                            >
                                {stagioni?.map(s => (
                                    <MenuItem key={s.id} value={s.id}>{s.nome}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label="Da giornata"
                                name="numero_giornate_from"
                                type="number"
                                value={form.numero_giornate_from}
                                onChange={handleChange}
                                required
                                error={touched.numero_giornate_from && (!form.numero_giornate_from || Number(form.numero_giornate_from) <= 0)}
                                helperText={touched.numero_giornate_from && (!form.numero_giornate_from || Number(form.numero_giornate_from) <= 0) ? 'Minimo 1' : ' '}
                                InputProps={{ startAdornment: <InputAdornment position="start"><NumbersIcon /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                label="A giornata"
                                name="numero_giornate_to"
                                type="number"
                                value={form.numero_giornate_to}
                                onChange={handleChange}
                                required
                                error={touched.numero_giornate_to && (!form.numero_giornate_to || Number(form.numero_giornate_to) < Number(form.numero_giornate_from))}
                                helperText={touched.numero_giornate_to && (!form.numero_giornate_to || Number(form.numero_giornate_to) < Number(form.numero_giornate_from)) ? 'Deve essere ≥ Da giornata' : ' '}
                                InputProps={{ startAdornment: <InputAdornment position="start"><NumbersIcon /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Prefisso nome giornata"
                                name="nome_base_giornata"
                                value={form.nome_base_giornata}
                                onChange={handleChange}
                                required
                                inputProps={{ maxLength: 50 }}
                                error={touched.nome_base_giornata && !form.nome_base_giornata.trim()}
                                helperText={touched.nome_base_giornata && !form.nome_base_giornata.trim() ? 'Obbligatorio' : 'Esempio: "Giornata" → Giornata 1, Giornata 2...'}
                                InputProps={{ startAdornment: <InputAdornment position="start"><TextFieldsIcon /></InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    disabled={loading || !isValid()}
                                    sx={{ minWidth: 160, fontWeight: 700, py: 1.2 }}
                                >
                                    {loading ? <CircularProgress size={22} color="inherit" /> : 'Crea giornate'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    color="secondary"
                                    size="large"
                                    disabled={loading}
                                    sx={{ minWidth: 120, fontWeight: 700, py: 1.2 }}
                                    onClick={() => {
                                        setForm({
                                            legaId: '',
                                            stagioneId: '',
                                            numero_giornate_from: '',
                                            numero_giornate_to: '',
                                            nome_base_giornata: 'Giornata'
                                        });
                                        setTouched({});
                                        setResult(null);
                                        setError(null);
                                    }}
                                >
                                    Reset
                                </Button>
                                {result && (
                                    <Alert severity={result.esito === 1 ? 'success' : 'warning'} sx={{ flex: 1, minWidth: 220 }}>
                                        <b>{result.message || 'Operazione completata.'}</b><br />
                                        Creati: <b>{result.records_creati}</b> / Skippati: <b>{result.records_skippati}</b>
                                    </Alert>
                                )}
                                {error && (
                                    <Alert severity="error" sx={{ flex: 1, minWidth: 220 }}>{error}</Alert>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </CardContent>
        </Card>
    );
};

export default ToolsGiornate;
